import clsx from "clsx";
import { memo, ReactElement, useContext, type MouseEvent } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  GridTableCell,
  GridTableDetailsPanel,
  IGridRowProps,
  TableWrapperContext,
  useTableRowIsSelected,
} from "@ryde/controls";
import { usePageContext, useRegisterSpecificTableRowId } from "@ryde/redux";
import {
  draggingBetweenRidesGridIdSelector,
  generateDropIdGridItem,
  IRideTableDto,
  isHighlightedAfterEditSF,
  isRowExpandedSF,
  isSingleSelectedRideIdSF,
  RideCrudModeKey,
  selectedSingleRowRideIdAtom,
  selectedTableRowsIdsAtomFamily,
} from "@ryde/shared";

import { GridRowDroppable } from "./grid-row-droppable";

const GridRowRendererRidesComponent = ({
  row,
  renderSubComponent,
}: IGridRowProps<IRideTableDto>): ReactElement => {
  const { original } = row;
  const rideId = original.id;
  const { entity } = usePageContext();
  const isHighlightedAfterEdit = useRecoilValue(
    isHighlightedAfterEditSF([entity, rideId]),
  );
  const { isGridSingleSelectView } = useContext(TableWrapperContext);

  useRegisterSpecificTableRowId(rideId);

  const isRideBulkSelected = useTableRowIsSelected(entity, rideId);
  const isSingleSelectedRow = useRecoilValue(isSingleSelectedRideIdSF(rideId));
  const isSelectedRow =
    entity === "ride" && isGridSingleSelectView
      ? isSingleSelectedRow
      : isRideBulkSelected;
  const rideDragId = useRecoilValue(draggingBetweenRidesGridIdSelector);
  const isDraggingOtherRide = Boolean(rideDragId) && rideDragId !== rideId;

  const isRowExpanded = useRecoilValue(isRowExpandedSF([entity, rideId]));

  const setSelectedSingleRowRideId = useSetRecoilState(
    selectedSingleRowRideIdAtom,
  );
  const setRideGridSelectedIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily(RideCrudModeKey.uiUnit),
  );

  const handleSingleSelectRowClick = useEvent(
    (event: MouseEvent<HTMLTableRowElement>): void => {
      if (entity !== "ride" || !isGridSingleSelectView) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (
        target.closest(
          "a, button, input, textarea, select, label, [contenteditable='true']",
        )
      ) {
        return;
      }
      if (target.closest('[data-e2e="drag-grip-vertical"]')) {
        return;
      }
      setSelectedSingleRowRideId(rideId);
      setRideGridSelectedIds([rideId]);
    },
  );

  return (
    <>
      <GridRowDroppable
        key={`${row.id}_${rideId}`}
        droppableId={generateDropIdGridItem(rideId) + "_" + row.id}
        row={row}
        className={clsx({
          expanded: isRowExpanded && !isDraggingOtherRide,
          highlighted:
            isHighlightedAfterEdit || (isGridSingleSelectView && isSelectedRow),
          [`tr_ride_id_${rideId}`]: !!rideId,
        })}
        isDroppable={!isRowExpanded || isDraggingOtherRide}
        onRowClick={
          entity === "ride" && isGridSingleSelectView
            ? handleSingleSelectRowClick
            : undefined
        }
      >
        {row.getVisibleCells().map(cell => (
          <GridTableCell<IRideTableDto>
            key={`${cell.id}_${rideId}`}
            cell={cell}
          />
        ))}
      </GridRowDroppable>
      {renderSubComponent && (
        <GridTableDetailsPanel
          row={row}
          key={`${row.id}_${rideId}_details_panel`}
          renderSubComponent={renderSubComponent}
        />
      )}
    </>
  );
};

export const GridRowRendererRides = memo(
  GridRowRendererRidesComponent,
  (previousProps, nextProps) =>
    previousProps.row === nextProps.row &&
    previousProps.renderSubComponent === nextProps.renderSubComponent &&
    previousProps.columnsLayoutSignature === nextProps.columnsLayoutSignature,
);
